import { z } from 'zod';
import { EvidenceIdSchema, TransactionIdSchema } from './common.js';
/**
 * `POST /v1/evidence`, `POST /v1/transactions/{id}/evidence` (SAD §8, M-01).
 *
 * Two-step upload: `POST /v1/evidence` mints a short-TTL pre-signed upload URL (the depositor's
 * client uploads the slip image directly to object storage); `POST /v1/transactions/{id}/evidence`
 * then submits either that `evidence_id`, a raw `qr_payload`, or both, which triggers verification.
 */
/** Evidence kinds. Phase 1 has exactly one — a bank transfer slip (SAD §6/§8). */
export const EVIDENCE_KINDS = ['BANK_SLIP'];
export const EvidenceKindSchema = z.enum(EVIDENCE_KINDS);
// ---------------------------------------------------------------------------------------------
// POST /v1/evidence — pre-signed upload URL
// ---------------------------------------------------------------------------------------------
/**
 * Request body for `POST /v1/evidence`.
 *
 * SAD §8 shows only the response shape for this call; the request fields below (the evidence
 * kind, and the content type so the pre-signed URL can pin it) are a judgment call — see this
 * package's delivery report.
 */
export const CreateEvidenceUploadRequestSchema = z
    .object({
    kind: EvidenceKindSchema,
    content_type: z.enum(['image/jpeg', 'image/png', 'application/pdf']).optional(),
})
    .strict();
/**
 * `201` response for `POST /v1/evidence` (SAD §8):
 * ```
 * { "evidence_id":"ev_...", "upload_url":"https://r2...&sig=...", "expires_in":300 }
 * ```
 */
export const CreateEvidenceUploadResponseSchema = z
    .object({
    evidence_id: EvidenceIdSchema,
    upload_url: z.string().url(),
    expires_in: z.number().int().positive(),
    /**
     * Headers the PUT must send verbatim alongside the URL. Under SSE-C the URL is signed over
     * these, so a PUT without them is refused by the object store. For the participant's
     * *server* only — they contain that participant's derived encryption key, and a browser
     * must never see them. Empty when encryption at rest is off (dev stacks).
     */
    upload_headers: z.record(z.string()).default({}),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// POST /v1/transactions/{id}/evidence — mandatory slip → triggers verification
// ---------------------------------------------------------------------------------------------
/**
 * Request body for `POST /v1/transactions/{id}/evidence` (SAD §8):
 * ```
 * { "kind":"BANK_SLIP", "qr_payload":"0038000600...", "evidence_id":"ev_..." }
 * ```
 * At least one of `qr_payload` (preferred — SAD §6) or `evidence_id` (an uploaded image) must be
 * present.
 */
export const SubmitEvidenceRequestSchema = z
    .object({
    kind: EvidenceKindSchema,
    qr_payload: z.string().min(1).optional(),
    evidence_id: EvidenceIdSchema.optional(),
})
    .strict()
    .refine((body) => body.qr_payload !== undefined || body.evidence_id !== undefined, {
    message: 'at least one of qr_payload or evidence_id is required',
    path: ['qr_payload'],
});
/**
 * `202` response for `POST /v1/transactions/{id}/evidence` (SAD §8) — accepted; withdrawal
 * LOCKED, transaction VERIFYING. The verification result arrives via webhook + `GET`, not here.
 */
export const SubmitEvidenceResponseSchema = z
    .object({
    id: TransactionIdSchema,
    status: z.literal('VERIFYING'),
})
    .strict();
//# sourceMappingURL=evidence.js.map